import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Skeleton,
  Button
} from '@mui/material';
import { Close as CloseIcon, CheckCircleOutline as CheckCircleOutlineIcon } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nPersonasNs, I18nCoreNs } from '@/i18n/dictionaries/interfaces';
import { getLanguageDisplayName } from '@/app/utils/languageUtils';
import type { ScenarioPersonaDto, PersonaDto } from '@/api-gen/api.schemas';
import { PersonaUsage as PersonaUsageEnum } from '@/api-gen/api.schemas';
import { getAvatarUrlWithSas } from '@/app/utils/avatarUtils';
import { usePersonaGetPersona } from '@/api-gen/api';
import { PersonaSharingChip } from '@/features/personas';
import ScenarioPersonaRoleSelector from './ScenarioPersonaRoleSelector';

interface ScenarioPersonaCardProps {
  scenarioPersona: ScenarioPersonaDto;
  onRemove: (personaId: number) => void;
  onSetDefault: (personaId: number) => void;
  onEdit?: (persona: PersonaDto) => void;
  scenarioLanguage?: string;
  scenarioCompanyId?: number;
  onChangeRoleId?: (personaId: number, roleId: number | null) => void;
}

const ScenarioPersonaCard: React.FC<ScenarioPersonaCardProps> = React.memo(
  ({ scenarioPersona, onRemove, onSetDefault, onEdit, scenarioLanguage, scenarioCompanyId, onChangeRoleId }) => {
    const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
    const [translatePersonas] = useTranslation([I18nNamespace.Personas]);
    const [translateCore] = useTranslation([I18nNamespace.Core]);
    const [avatarLoadError, setAvatarLoadError] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);

    // If persona data is not included in the scenario data, fetch it separately
    const { data: fetchedPersona, isLoading: isPersonaLoading } = usePersonaGetPersona(scenarioPersona.personaId || 0, {
      query: {
        enabled: !scenarioPersona.persona && !!scenarioPersona.personaId,
        refetchOnMount: false,
        refetchOnWindowFocus: false
      }
    });

    // Use either the embedded persona data or the fetched persona data
    const persona = scenarioPersona.persona || (fetchedPersona?.data as PersonaDto);
    const usages = scenarioPersona.personaUsages || [];
    const isDefault = usages.includes(PersonaUsageEnum.Default);

    const handleAvatarError = useCallback(() => {
      if (mountedRef.current) {
        setAvatarLoadError(true);
      }
    }, []);

    const getInitials = (name?: string) => {
      if (!name) return 'P';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    };

    const getAvatarColor = (id?: number) => {
      const colors = ['#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#c2185b', '#0288d1', '#0097a7', '#00796b'];
      return colors[(id || 0) % colors.length];
    };

    const handleRemove = () => {
      if (persona?.id) {
        onRemove(persona.id);
      }
    };

    const handleCardClick = () => {
      if (persona && onEdit) {
        onEdit(persona);
      }
    };

    // Show loading skeleton while fetching persona data
    if (!persona && isPersonaLoading) {
      return (
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box display="flex" alignItems="center" mb={1.5}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1.5 }} />
              <Box flex={1}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="40%" height={14} />
              </Box>
            </Box>
            <Skeleton variant="text" width="100%" height={14} />
            <Skeleton variant="text" width="80%" height={14} sx={{ mb: 1 }} />
          </CardContent>
        </Card>
      );
    }

    // If no persona data is available and not loading, don't render
    if (!persona) {
      return null;
    }

    const hasLanguageMismatch = !!(
      scenarioLanguage &&
      persona?.languageCode &&
      persona.languageCode !== scenarioLanguage
    );

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          cursor: onEdit ? 'pointer' : 'default',
          border: '1px solid',
          borderColor: 'grey.200',
          '&:hover': onEdit
            ? {
                boxShadow: 2
              }
            : {},
          '&:hover .set-default-action': {
            opacity: isDefault ? 0 : 1,
            pointerEvents: isDefault ? 'none' : 'auto'
          }
        }}
        onClick={onEdit ? handleCardClick : undefined}
      >
        {/* Default indicator - colored top border accent */}
        {isDefault && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: 'primary.main',
              borderTopLeftRadius: 'inherit',
              borderTopRightRadius: 'inherit',
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Close button in top-right corner */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click when removing
            handleRemove();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Language mismatch indicator - subtle left border */}
        {hasLanguageMismatch && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: 4,
              bgcolor: 'warning.main',
              pointerEvents: 'none'
            }}
          />
        )}

        <CardContent sx={{ flexGrow: 1, pr: 5, pb: '8px !important' }}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Avatar
              sx={{
                bgcolor: getAvatarColor(persona.id),
                width: 40,
                height: 40,
                fontSize: 14,
                mr: 1.5
              }}
              src={!avatarLoadError && persona.avatarUrl ? getAvatarUrlWithSas(persona.avatarUrl) : undefined}
              alt={persona.name || 'Persona avatar'}
              imgProps={{ onError: handleAvatarError }}
            >
              {getInitials(persona.name)}
            </Avatar>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.2
                  }}
                >
                  {persona.name ||
                    translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona))}
                </Typography>
                {isDefault && (
                  <Chip
                    size="small"
                    label={translate(
                      nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.usageTypes.default)
                    )}
                    color="primary"
                    variant="filled"
                    sx={{ fontSize: '0.75rem', height: 22 }}
                  />
                )}
                <PersonaSharingChip
                  persona={persona}
                  tooltipText={translatePersonas(nameof.full<I18nPersonasNs>((n) => n.card.sharedWithTooltip))}
                  size="small"
                />
              </Box>
              {persona.languageCode && (
                <Typography variant="body2" color={hasLanguageMismatch ? 'warning.main' : 'text.secondary'} noWrap>
                  {getLanguageDisplayName(persona.languageCode, translateCore)} • {persona.ttsProvider}
                </Typography>
              )}
            </Box>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2em',
              mb: 1
            }}
          >
            {persona.description ||
              translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.noDescriptionProvided))}
          </Typography>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', alignItems: 'center', pt: 0, px: 2, pb: 1.5 }}>
          {/* Role selector in lower left */}
          {scenarioLanguage && (
            <Box onClick={(e) => e.stopPropagation()}>
              <ScenarioPersonaRoleSelector
                value={scenarioPersona.roleId ?? null}
                onChange={(rid) => persona?.id && onChangeRoleId && onChangeRoleId(persona.id, rid)}
                langCode={scenarioLanguage}
                scenarioCompanyId={scenarioCompanyId}
                size="small"
              />
            </Box>
          )}

          {/* Set as Default button in lower right (on hover) */}
          <Box
            className="set-default-action"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s ease',
              pointerEvents: 'none',
              visibility: isDefault ? 'hidden' : 'visible'
            }}
          >
            <Button
              size="small"
              variant="text"
              disabled={isDefault}
              onClick={(e) => {
                e.stopPropagation();
                if (persona?.id && !isDefault) onSetDefault(persona.id);
              }}
            >
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.setAsDefault))}
            </Button>
          </Box>
        </CardActions>
      </Card>
    );
  }
);

ScenarioPersonaCard.displayName = 'ScenarioPersonaCard';

export default ScenarioPersonaCard;
